// Bolsa Fish/web/src/shared/components/ConfirmModal.tsx
// ------unified admin styling----
import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

interface ConfirmModalProps {
  isOpen: boolean;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  headingLevel?: 'h2' | 'h5';
}

export default function ConfirmModal({
  isOpen,
  message,
  onConfirm,
  onCancel,
  headingLevel = 'h2',
}: ConfirmModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);

  // Focus trap for accessibility (Tab, Shift+Tab, Escape)
  useEffect(() => {
    const modal = modalRef.current;
    const firstFocusable = firstFocusableRef.current;

    if (isOpen && modal && firstFocusable) {
      firstFocusable.focus();
      const focusableElements = modal.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const first = focusableElements[0] as HTMLElement;
      const last = focusableElements[focusableElements.length - 1] as HTMLElement;

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onCancel();
        }
        if (e.key === 'Tab') {
          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      };

      modal.addEventListener('keydown', handleKeyDown);
      return () => modal.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  const Heading = headingLevel;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirmModalLabel"
    >
      <div
        className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full"
        ref={modalRef}
        tabIndex={-1}
      >
        <div className="flex justify-between items-center mb-4">
          <Heading id="confirmModalLabel" className="text-lg font-semibold text-gray-800">
            Confirm Action
          </Heading>
          <button
            onClick={onCancel}
            className="text-gray-600 hover:text-gray-800"
            aria-label="Close"
          >
            <X size={24} />
          </button>
        </div>
        <p className="text-gray-700 mb-4">{message}</p>
        <div className="flex justify-end space-x-2">
          <button
            className="px-4 py-2 bg-gray-300 rounded-lg hover:bg-gray-400 transition"
            onClick={onCancel}
            ref={firstFocusableRef}
            aria-label="Cancel action"
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
            onClick={onConfirm}
            aria-label="Confirm action"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}
